import React, { useState, useEffect, useContext } from 'react';
import {
  IonSearchbar,
  IonButton,
  IonIcon,
  IonPage,
  IonContent,
  IonBadge,
  IonLoading
} from '@ionic/react';
import { navigate } from 'ionicons/icons';
import './LaboratorySelection.scss';
import { AppointmentStepProps } from '../../navigators/AppointmentNavigator';
import Stepper from '../../components/Stepper';
import AppHeader from '../../components/AppHeader';
import { connect } from 'react-redux';
import { setAppointment } from '../../store/actions/appointments';
import { AppointmentsRootState } from '../../store/reducers/appointments';
import LaboratoryCard from '../../components/LaboratoryCard';
import { Appointment, Laboratory } from '../../models';
import LaboratoryRepository from '../../repositories/LaboratoryRepository';
import EventBus from '../../services/EventBus';
import { AuthenticationRootState } from '../../store/reducers/authentication';
import PatientRepository from '../../repositories/PatientRepository';
import { ThemeContext } from '../../components/theme/ThemeProvider';
import { useLocation } from 'react-router-dom';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}
const mapStateToProps = (
  state: {
    appointments: AppointmentsRootState;
    authentication: AuthenticationRootState;
  },
  ownProps: any
) => {
  return {
    user: state.authentication.user,
    laboratory: state.appointments.appointment.laboratory
  };
};

const mapDispatchToProps = (dispatch: Function, ownProps: any) => ({
  selectLaboratory: (appointment: Partial<Appointment>) =>
    dispatch(setAppointment(appointment))
});

type LaboratorySelectionProps = ReturnType<typeof mapDispatchToProps> &
  ReturnType<typeof mapStateToProps> & { isCovid: boolean };

const LaboratorySelection: React.FC<
  AppointmentStepProps & LaboratorySelectionProps
> = ({
  step,
  selectLaboratory,
  user,
  history,
  nextStepRouterLink = '',
  isCovid = false
}) => {
  const theme = useContext(ThemeContext);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearchLoading, setSearchIsLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [laboratories, setLaboratories] = useState<Laboratory[]>([]);
  const [recentLaboratories, setRecentLaboratories] = useState<Laboratory[]>(
    []
  );
  const [defaultLaboratories, setDefaultLaboratories] = useState<Laboratory[]>(
    []
  );

  let specialty = useQuery().get('specialite');

  useEffect(() => {
    if (searchText.length > 2) {
      setSearchIsLoading(true);

      const laboratoriesFetchParams = [['covid', +isCovid]];

      if (specialty) {
        laboratoriesFetchParams.push(['speciality', specialty]);
      }

      LaboratoryRepository.findByName(searchText, laboratoriesFetchParams).then(
        (laboratories) => {
          setLaboratories(laboratories);
          setSearchIsLoading(false);
        }
      );
    }
  }, [isCovid, searchText, specialty]);

  useEffect(() => {
    function filterLaboratories(laboratories: Laboratory[]) {
      return laboratories.filter(
        (laboratory, index, self) =>
          index === self.findIndex((t) => t.id === laboratory.id) &&
          laboratory.covid === isCovid &&
          laboratory.appointment_type !== 'none' &&
          laboratory.appointment_type !== 'direct_only'
      );
    }

    async function fetchSlots() {
      setIsLoading(true);

      const laboratoriesFetchParams = [
        ['appointments', 1],
        ['covid', +isCovid]
      ];

      if (specialty) {
        laboratoriesFetchParams.push(['speciality', specialty]);
      }

      try {
        if (user?.id) {
          const patients = await PatientRepository.findAll();
          const laboratories = patients
            .map((patient) => patient.appointments)
            .flat()
            .map((appointment) => appointment.laboratory!);

          if (laboratories.length) {
            setRecentLaboratories(filterLaboratories(laboratories));
          } else {
            setDefaultLaboratories(
              filterLaboratories(
                await LaboratoryRepository.findAll(laboratoriesFetchParams)
              )
            );
          }
        } else {
          setDefaultLaboratories(
            filterLaboratories(
              await LaboratoryRepository.findAll(laboratoriesFetchParams)
            )
          );
        }

        setIsLoading(false);
      } catch (e) {
        setIsLoading(false);
      }
    }

    fetchSlots();
  }, [isCovid, user, specialty]);

  function locate() {
    if (navigator.geolocation) {
      setIsLoading(true);
      navigator.geolocation.getCurrentPosition(
        function (position) {
          let lat = position.coords.latitude;
          let lng = position.coords.longitude;

          const laboratoriesFetchParams = [['covid', +isCovid]];

          if (specialty) {
            laboratoriesFetchParams.push(['speciality', specialty]);
          }

          LaboratoryRepository.findByLatLng(
            { lat, lng },
            laboratoriesFetchParams
          ).then((laboratories) => {
            setLaboratories(laboratories);
            setIsLoading(false);
          });
        },
        (e) => {
          EventBus.error(
            "La géolocalisation n'est pas disponible sur votre appareil. Merci de vérifier que la géolocalisation est bien activée dans vos réglages"
          );
          setIsLoading(false);
        }
      );
    } else {
      EventBus.error(
        'La géolocolisation de semble pas disponible sur cet appareil. Merci de vérifier que la géolocalisation est bien activée dans vos réglages.'
      );
      console.warn(
        'Geolocation is not supported for this Browser/OS version yet.'
      );
    }
  }

  return (
    <IonPage>
      <AppHeader defaultBackLink="/" />
      <Stepper step={step} />
      <IonContent>
        <div className="wrapper">
          <div className="ion-padding">
            {theme.laboratorySelection ? (
              <div className="ion-margin-bottom ion-text-center">
                <h2>{theme.laboratorySelection.title}</h2>
                <div>{theme.laboratorySelection.intro}</div>
              </div>
            ) : null}
            <IonSearchbar
              debounce={0}
              value={searchText}
              onIonChange={(e) => {
                if (e.detail.value === undefined) return;
                setSearchText(e.detail.value!);
              }}
              onKeyUp={(e) => {
                if (e.which === 13) {
                  (e.target as HTMLInputElement).blur();
                }
              }}
              autocorrect="off"
              placeholder="Où (adresse, ville...)"
            />
            <IonButton fill="clear" onClick={locate}>
              <IonIcon icon={navigate} /> Autour de moi
            </IonButton>
            {laboratories.length ? (
              laboratories.map((laboratory, i) => (
                <LaboratoryCard
                  key={laboratory.id}
                  laboratory={laboratory}
                  onSelect={() => {
                    history.push(nextStepRouterLink);
                  }}
                />
              ))
            ) : recentLaboratories.length &&
              searchText.length > 2 &&
              !isLoading ? (
              <div className="ion-padding ion-text-center">
                <IonBadge>
                  Aucun résultat, veuillez faire une recherche
                </IonBadge>
              </div>
            ) : null}
            {recentLaboratories.length ? (
              <>
                <h3 className="ion-text-center">{`Laboratoire${
                  recentLaboratories.length > 1 ? 's' : ''
                } récent${recentLaboratories.length > 1 ? 's' : ''}`}</h3>
                {recentLaboratories.map((laboratory, i) => (
                  <LaboratoryCard
                    key={laboratory.id}
                    laboratory={laboratory}
                    onSelect={() => {
                      selectLaboratory({ laboratory: laboratory });
                      history.push(nextStepRouterLink);
                    }}
                  />
                ))}
              </>
            ) : null}
            {!laboratories.length &&
            !recentLaboratories.length &&
            searchText.length &&
            !isSearchLoading ? (
              <div className="ion-padding ion-text-center">
                <IonBadge>
                  Aucun résultat, veuillez afficher votre recherche
                </IonBadge>
              </div>
            ) : null}
            {!recentLaboratories.length && defaultLaboratories.length ? (
              <>
                <h3 className="ion-text-center">
                  Nos laboratoires disponibles en ligne
                </h3>
                {defaultLaboratories.map((laboratory, i) => (
                  <LaboratoryCard
                    key={laboratory.id}
                    laboratory={laboratory}
                    onSelect={() => {
                      selectLaboratory({ laboratory: laboratory });
                      history.push(nextStepRouterLink);
                    }}
                  />
                ))}
              </>
            ) : null}
          </div>
        </div>
      </IonContent>
      <IonLoading isOpen={isLoading} />
    </IonPage>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(LaboratorySelection);
